import React, { useState, useEffect } from 'react';
import { CardComponentProps } from '@/lib/cards';
import { getBillingStatus, getBillingPortalUrl, BillingStatus } from '@/api/billing';
import { getUsers, UserSummary } from '@/api/users';

/**
 * Organisation Overview Card
 */
export const OrgOverviewCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const [billingData, usersData] = await Promise.all([
          getBillingStatus(),
          userContext.orgId ? getUsers({ orgId: userContext.orgId, limit: 25 }) : null,
        ]);
        setBilling(billingData);
        if (usersData) {
          setMemberCount(usersData.items.length + (usersData.nextCursor ? 1 : 0));
        }
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [userContext.orgId, onError]);

  if (loading) return <div style={{ padding: '1rem', color: '#666' }}>Loading...</div>;
  if (!billing) return null;

  const statusColor =
    billing.status === 'active' || billing.status === 'trialing' ? '#28a745' : '#dc3545';

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'grid', gap: '0.75rem' }}>
        {userContext.orgId && (
          <div>
            <strong>Organisation ID:</strong>{' '}
            <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{userContext.orgId}</span>
          </div>
        )}
        {memberCount !== null && (
          <div>
            <strong>Members:</strong> {memberCount}
          </div>
        )}
        <div>
          <strong>Plan:</strong>{' '}
          <span style={{ textTransform: 'capitalize' }}>{billing.plan ?? 'None'}</span>
        </div>
        <div>
          <strong>Status:</strong>{' '}
          <span style={{ color: statusColor, textTransform: 'capitalize' }}>
            {billing.status ?? 'Unknown'}
          </span>
        </div>
        {billing.trialEndsAt && (
          <div>
            <strong>Trial ends:</strong> {new Date(billing.trialEndsAt).toLocaleDateString()}
          </div>
        )}
        {billing.reason && (
          <div style={{ fontSize: '0.9rem', color: '#666' }}>{billing.reason}</div>
        )}
      </div>
    </div>
  );
};

OrgOverviewCard.displayName = 'OrgOverviewCard';

/**
 * Organisation Members Card
 */
export const OrgMembersCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [members, setMembers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const response = await getUsers({ orgId: userContext.orgId ?? undefined, limit: 50 });
        setMembers(response.items);
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [userContext.orgId, onError]);

  if (loading) return <div style={{ padding: '1rem', color: '#666' }}>Loading...</div>;

  if (members.length === 0) {
    return <div style={{ padding: '1rem', color: '#666' }}>No members found.</div>;
  }

  return (
    <div style={{ padding: '1rem' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Name</th>
            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Tier</th>
            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Joined</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <tr key={member.id} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '0.5rem' }}>{member.displayName}</td>
              <td style={{ padding: '0.5rem' }}>
                <span
                  style={{
                    backgroundColor: '#e7f3ff',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '2px',
                    fontSize: '0.85rem',
                  }}
                >
                  {member.tier.replace(/_/g, ' ')}
                </span>
              </td>
              <td style={{ padding: '0.5rem' }}>
                {new Date(member.createdAt).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

OrgMembersCard.displayName = 'OrgMembersCard';

/**
 * Organisation Billing Card
 */
export const OrgBillingCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [data, setData] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const billing = await getBillingStatus();
        setData(billing);
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [onError]);

  const handleManagePortal = async () => {
    try {
      setActionInProgress(true);
      const url = await getBillingPortalUrl();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionInProgress(false);
    }
  };

  if (loading) return <div style={{ padding: '1rem', color: '#666' }}>Loading...</div>;
  if (!data) return null;

  const statusColor =
    data.status === 'active' || data.status === 'trialing' ? '#28a745' : '#dc3545';

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
        <div>
          <strong>Plan:</strong>{' '}
          <span style={{ textTransform: 'capitalize' }}>{data.plan ?? 'None'}</span>
        </div>
        <div>
          <strong>Status:</strong>{' '}
          <span style={{ color: statusColor, textTransform: 'capitalize' }}>
            {data.status ?? 'Unknown'}
          </span>
        </div>
        {data.trialEndsAt && (
          <div>
            <strong>Trial ends:</strong> {new Date(data.trialEndsAt).toLocaleDateString()}
            {' '}({data.trialEmailsProcessed} emails processed)
          </div>
        )}
        <div>
          <strong>Stripe customer:</strong> {data.hasStripeCustomer ? 'Yes' : 'No'}
        </div>
      </div>

      {data.hasStripeCustomer && (
        <button
          onClick={handleManagePortal}
          disabled={actionInProgress}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: actionInProgress ? 'not-allowed' : 'pointer',
            opacity: actionInProgress ? 0.6 : 1,
          }}
        >
          Manage billing
        </button>
      )}
    </div>
  );
};

OrgBillingCard.displayName = 'OrgBillingCard';

/**
 * Organisation Audit Log Card
 */
export const OrgAuditLogCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  return (
    <div style={{ padding: '1rem' }}>
      <div
        style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#f9f9f9',
          borderLeft: '3px solid #ccc',
          color: '#666',
          fontSize: '0.9rem',
        }}
      >
        Organisation audit log is not yet available.
      </div>
    </div>
  );
};

OrgAuditLogCard.displayName = 'OrgAuditLogCard';

/**
 * Future Write Actions Card (placeholder)
 */
export const FutureWriteActionsCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  return (
    <div style={{ padding: '1rem' }}>
      <div
        style={{
          padding: '1rem',
          backgroundColor: '#e7f3ff',
          border: '1px solid #0066cc',
          borderRadius: '4px',
          color: '#0066cc',
        }}
      >
        <strong>Coming Soon</strong>
        <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
          The following features are coming in a future release:
          <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
            <li>Organisation category sets</li>
            <li>Organisation policy rules</li>
            <li>Limited webhook repair</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

FutureWriteActionsCard.displayName = 'FutureWriteActionsCard';

export default {
  OrgOverviewCard,
  OrgMembersCard,
  OrgBillingCard,
  OrgAuditLogCard,
  FutureWriteActionsCard,
};
